import { useState, useEffect } from 'react';
import Header from '../components/Header';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';

const ROLE_LABELS = {
  salesman:       'مندوب مبيعات',
  supervisor:     'مشرف',
  area_supervisor: 'مشرف منطقة',
};

const ROLE_COLORS = {
  salesman:       '#047857',
  supervisor:     '#1d4ed8',
  area_supervisor: '#7c3aed',
};

export default function PayoutsPage() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(null);
  const [form, setForm]       = useState({});
  const toast = useToast();

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try { setPayouts(await api.get('/payouts')); }
    catch(err) { console.error("Payouts error:",err); toast.error("خطأ: " + err.message); }
    finally { setLoading(false); }
  }

  function startEdit(payout) {
    setEditing(payout.role_code);
    setForm({
      max_qty_type1: payout.max_qty_type1,
      max_qty_type3: payout.max_qty_type3,
      max_qty_type4: payout.max_qty_type4,
      max_qty_type5: payout.max_qty_type5,
      max_bad_return: payout.max_bad_return,
      max_coverage:   payout.max_coverage,
      max_collection: payout.max_collection,
      max_specific:   payout.max_specific,
    });
  }

  async function save(roleCode) {
    setSaving(roleCode);
    try {
      await api.put(`/payouts/${roleCode}`, Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, parseFloat(v) || 0])
      ));
      toast.success('تم حفظ حدود العمولة');
      setEditing(null);
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(null); }
  }

  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (loading) return (
    <div className="page">
      <Header title="حدود العمولة" />
      <div className="loading-page"><div className="spinner" /></div>
    </div>
  );

  return (
    <div className="page">
      <Header title="حدود العمولة القصوى" />
      <div className="page-content">

        <div style={styles.notice}>
          <span>ℹ️</span>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>هذه الحدود تحدد الحد الأقصى للعمولة لكل مؤشر</p>
            <p style={{ fontSize: 11, color: 'var(--gray-500)', margin: '2px 0 0' }}>
              التعديلات تؤثر على احتساب العمولات الجديدة فقط — لا تؤثر على النتائج المحسوبة مسبقاً
            </p>
          </div>
        </div>

        {payouts.filter(p => ROLE_LABELS[p.role_code]).map(payout => {
          const isEdit = editing === payout.role_code;
          const color  = ROLE_COLORS[payout.role_code] || 'var(--primary)';
          const totalMax = isEdit
            ? [form.max_qty_type1, form.max_qty_type3, form.max_qty_type4, form.max_qty_type5,
               form.max_bad_return, form.max_coverage, form.max_collection, form.max_specific]
               .reduce((s, v) => s + (parseFloat(v) || 0), 0)
            : [payout.max_qty_type1, payout.max_qty_type3, payout.max_qty_type4, payout.max_qty_type5,
               payout.max_bad_return, payout.max_coverage, payout.max_collection, payout.max_specific]
               .reduce((s, v) => s + (parseFloat(v) || 0), 0);

          return (
            <div key={payout.role_code} style={{ ...styles.card, borderTop: `3px solid ${color}` }}>
              <div style={styles.cardHeader}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color }}>{ROLE_LABELS[payout.role_code]}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>
                    الحد الأقصى الإجمالي: <strong style={{ color }}>{totalMax.toLocaleString('ar', { maximumFractionDigits: 0 })} ر.س</strong>
                  </div>
                </div>
                {!isEdit ? (
                  <button className="btn btn-outline btn-sm" onClick={() => startEdit(payout)}>تعديل</button>
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => save(payout.role_code)} disabled={saving === payout.role_code}>
                      {saving === payout.role_code ? '...' : 'حفظ'}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>إلغاء</button>
                  </div>
                )}
              </div>

              <div className="divider" style={{ margin: '12px 0' }} />

              {/* Qty by item type */}
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8 }}>الكميات حسب النوع</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                {[
                  { key: 'max_qty_type1', label: 'دجاج مبرد',    color: '#047857' },
                  { key: 'max_qty_type3', label: 'دجاج مجمد',    color: '#1d4ed8' },
                  { key: 'max_qty_type4', label: 'مقطعات مبرد',  color: '#b45309' },
                  { key: 'max_qty_type5', label: 'دجاج مبرد GB', color: '#7c3aed' },
                ].map(item => (
                  <div key={item.key} style={styles.fieldBox}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: item.color, marginBottom: 4 }}>{item.label}</div>
                    {isEdit ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input className="input" type="number" value={form[item.key]}
                          onChange={e => s(item.key, e.target.value)}
                          style={{ padding: '6px 8px', fontSize: 13, textAlign: 'center' }} />
                        <span style={{ fontSize: 10, color: 'var(--gray-400)', flexShrink: 0 }}>ر.س</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 16, fontWeight: 800, color: item.color }}>
                        {parseFloat(payout[item.key]).toLocaleString('ar', { maximumFractionDigits: 0 })} ر.س
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Other KPIs */}
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8 }}>المؤشرات الأخرى</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { key: 'max_bad_return', label: 'المرتجع الرديء' },
                  { key: 'max_coverage',   label: 'التغطية' },
                  { key: 'max_collection', label: 'التحصيل' },
                  { key: 'max_specific',   label: 'الهدف الخاص' },
                ].map(item => (
                  <div key={item.key} style={styles.fieldBox}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-600)', marginBottom: 4 }}>{item.label}</div>
                    {isEdit ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input className="input" type="number" value={form[item.key]}
                          onChange={e => s(item.key, e.target.value)}
                          style={{ padding: '6px 8px', fontSize: 13, textAlign: 'center' }} />
                        <span style={{ fontSize: 10, color: 'var(--gray-400)', flexShrink: 0 }}>ر.س</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--gray-700)' }}>
                        {parseFloat(payout[item.key]).toLocaleString('ar', { maximumFractionDigits: 0 })} ر.س
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {payout.updated_at && (
                <p style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 10, textAlign: 'left' }}>
                  آخر تعديل: {new Date(payout.updated_at).toLocaleString('ar-SA')}
                  {payout.updated_by_name ? ` — ${payout.updated_by_name}` : ''}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  notice: { background: 'var(--primary-light)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 18 },
  card: { background: 'white', borderRadius: 14, padding: '16px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)', marginBottom: 16 },
  cardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  fieldBox: { background: 'var(--gray-50)', borderRadius: 8, padding: '10px 12px', border: '1px solid var(--gray-100)' },
};
